/**
 * What the coach page shows around a training verdict's line (#270): the
 * readout's eyebrow, its history disclosure, and the proposal's controls.
 *
 * Pure, so the rules are tested rather than judged on a screen. The iOS twin
 * is GroundworkKit's `VerdictPresentation`, and the words both render come
 * from the shared deck (`COACH_COPY` / `CoachCopy`), which
 * `scripts/check_copy_decks.py` keeps byte-identical.
 */
import type { OpenerProposal, OpenerVerdict } from '../../api/types'
import { COACH_COPY } from '../../copy/coach'
import { fill } from '../../copy/firstRun'

/**
 * The readout's eyebrow. A verdict this client does not know reads as
 * today's verdict: never as "train", which the trigger never speaks, and
 * never as a guessed band (#253, PC-I44).
 */
export function verdictEyebrow(verdict: string): string {
  switch (verdict) {
    case 'ease':
      return COACH_COPY.verdictEyebrowEase
    case 'deload':
      return COACH_COPY.verdictEyebrowDeload
    case 'rest':
      return COACH_COPY.verdictEyebrowRest
    default:
      return COACH_COPY.verdictEyebrowUnknown
  }
}

/**
 * #245's disclosure: how much health history the verdict rests on, in one of
 * three wordings with the record's own counts (PC-I43). Null when the server
 * sent no coverage: nothing is said rather than a number nobody measured.
 */
export function coverageLine(coverage: OpenerVerdict['coverage']): string | null {
  if (!coverage) return null
  const have = coverage.baseline_days
  const need = coverage.baseline_days_needed
  if (have <= 0) return COACH_COPY.verdictHistoryNone
  if (have < need) return fill(COACH_COPY.verdictHistoryShort, { have, need })
  return fill(COACH_COPY.verdictHistoryEnough, { have })
}

/** The proposal's controls, as one of three states. */
export type ProposalView =
  /** Nothing to answer: the line is dismissed with "Got it" alone. */
  | { kind: 'none' }
  /**
   * The offer. `decline` is "Keep my plan", which declines and folds the line
   * away; null under a rest verdict, where "Got it" stands in (PC-I41).
   * `quiet`: the accept is drawn as quiet text rather than the amber pill.
   */
  | { kind: 'offer'; accept: string; decline: string | null; scope: string; quiet: boolean }
  /** Accepted: a faint record line, and Undo while it still can (PC-I42). */
  | { kind: 'answered'; record: string; undo: boolean }

/**
 * Which controls the proposal gets (PC-I41, PC-I42).
 *
 * Offered only while the answer can still change: once the session under
 * the call has started, an easing would move targets under it, so the line
 * is left with "Got it" alone. "I'll ease" is offered only where the user's phone
 * shows eased targets; elsewhere the choice is theirs to carry out (PC-I18).
 */
export function proposalView(proposal: OpenerProposal | null | undefined): ProposalView {
  if (!proposal) return { kind: 'none' }
  if (proposal.answer === 'accepted') {
    return {
      kind: 'answered',
      record: proposal.eases ? COACH_COPY.proposalEasedRecord : COACH_COPY.proposalLighterRecord,
      undo: proposal.can_change,
    }
  }
  if (proposal.answer != null || !proposal.can_change) return { kind: 'none' }
  return {
    kind: 'offer',
    accept: proposal.eases ? COACH_COPY.proposalEase : COACH_COPY.proposalGoLighter,
    decline: proposal.declinable ? COACH_COPY.proposalKeepPlan : null,
    scope: COACH_COPY.proposalScope,
    // Under rest the lighter session is offered second, never as the
    // recommendation (PC-I27), so its accept is quiet text and the visual
    // weight follows the advice (PC-I59). Rest is the one verdict whose
    // offer has no decline (PC-I41), so the two go together.
    quiet: !proposal.declinable,
  }
}

/**
 * Whether "Got it" dismisses the line beside the proposal's controls: always,
 * except beside an offer that carries "Keep my plan", which is the dismissal
 * as well (PC-I41).
 */
export function showsGotIt(view: ProposalView): boolean {
  return view.kind !== 'offer' || view.decline === null
}
